import type { EventRole, EventStatus, RegistrationStatus } from '@growup/site-shared';
import type { StatusTone } from '@growup/ui';

export const ROLE_LABEL: Record<EventRole, string> = {
  visitor: 'Visitante',
  volunteer: 'Voluntário',
  streamer: 'Streamer',
};

export const ROLE_PLURAL: Record<EventRole, string> = {
  visitor: 'Visitantes',
  volunteer: 'Voluntários',
  streamer: 'Streamers',
};

export const EVENT_STATUS_LOOK: Record<EventStatus, { label: string; tone: StatusTone }> = {
  draft: { label: 'Rascunho', tone: 'neutral' },
  published: { label: 'Publicado', tone: 'success' },
  cancelled: { label: 'Cancelado', tone: 'danger' },
};

export const REGISTRATION_LOOK: Record<RegistrationStatus, { label: string; tone: StatusTone }> = {
  invited: { label: 'Convidado', tone: 'info' },
  declined: { label: 'Recusou o convite', tone: 'neutral' },
  pending: { label: 'Por aprovar', tone: 'warning' },
  confirmed: { label: 'Confirmado', tone: 'success' },
  waitlisted: { label: 'Lista de espera', tone: 'info' },
  rejected: { label: 'Recusado', tone: 'neutral' },
  cancelled: { label: 'Cancelado', tone: 'neutral' },
};

export const LEVEL_LABEL: Record<string, string> = {
  guest: 'Sem conta',
  user: 'Utilizador',
  member: 'Membro',
  associate: 'Associado',
};

// A mao: o dia da semana abreviado do Intl muda entre browsers.
const WEEKDAY = ['dom.', 'seg.', 'ter.', 'qua.', 'qui.', 'sex.', 'sáb.'];

/** `sáb., 12/12` para um dia de evento `aaaa-mm-dd`. */
export function formatEventDay(day: string): string {
  const weekday = WEEKDAY[new Date(`${day}T12:00:00Z`).getUTCDay()] ?? '';
  return `${weekday}, ${day.slice(8, 10)}/${day.slice(5, 7)}`;
}

const LISBON = 'Europe/Lisbon';

/** Os campos de data e hora do formulario estao em hora de Lisboa, seja qual for o fuso do PC. */
function lisbonParts(date: Date): Record<string, number> {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: LISBON,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(date);
  const out: Record<string, number> = {};
  for (const p of parts) if (p.type !== 'literal') out[p.type] = Number(p.value);
  return out;
}

const pad = (n: number | undefined) => String(n ?? 0).padStart(2, '0');

/** ISO (UTC) → `2026-11-14T10:00`, para o `DateTimeInput` do kit. */
export function isoToLisbonInput(iso: string | null): string {
  if (iso === null) return '';
  const p = lisbonParts(new Date(iso));
  return `${String(p.year)}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

/** `2026-11-14T10:00` (hora de Lisboa) → ISO (UTC). `null` se vazio ou invalido. */
export function lisbonInputToIso(value: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!m) return null;
  const [y, mo, d, h, mi] = m.slice(1).map(Number) as [number, number, number, number, number];
  const wanted = Date.UTC(y, mo - 1, d, h, mi);
  // O desvio de Lisboa nesse instante (0 ou +1 h); duas voltas acertam na mudanca de hora.
  let guess = wanted;
  for (let i = 0; i < 2; i++) {
    const p = lisbonParts(new Date(guess));
    const shown = Date.UTC(p.year ?? 0, (p.month ?? 1) - 1, p.day, p.hour, p.minute);
    guess += wanted - shown;
  }
  return new Date(guess).toISOString();
}
